import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";

import { ETICHETTA_ACCESA, RIMANDO } from "@/components/home/stile";

/** «Dalla fonte alla decisione.» (§ 7 bis): una scheda sola con i manuali
 *  aperti dell'Osservatorio, sotto l'Accoglienza. Titoli e testi delle
 *  colonne sono quelli delle pagine; l'intestazione è del modello.
 *
 *  Dal 25/10/2026 (richiesta del committente: tre servizi diversi in una
 *  scheda sola erano troppi) le colonne sono **due**, OSINT e cibernetica,
 *  cioè il metodo: dalla fonte all'informazione, dall'informazione alla
 *  decisione. L'AI esce da qui (resta su /ai, e il suo disegno `Posto`
 *  resta esportato per /rullo e la pagina del dispaccio). Con due colonne
 *  c'è posto: numero e nome, titolo, disegno più grande, testo e rimando.
 *
 *  I disegni sono quelli del modello (300 × 120), non le figure di /osint e
 *  /cibernetica (`ImbutoOsint`, `AnelloControllo`): quelle sono larghe 640
 *  e scritte a 9-10 unità, e in una colonna come questa le scritte
 *  scenderebbero a cinque pixel. Il modello li anima con SMIL; qui è CSS
 *  (`.oi-pa-mx` in globals.css), con gli stessi tempi, e il movimento
 *  ridotto li ferma in una posa. */
export function DallaFonte() {
  return (
    <section aria-labelledby="pa-mx" className="oi-pa-mix mt-7 border border-ink border-t-4 border-t-segnale">
      <div className="grid grid-cols-1 items-end gap-x-10 gap-y-1 border-b border-rule px-5 pt-[22px] pb-[22px] min-[901px]:grid-cols-[auto_minmax(0,1fr)] min-[901px]:px-9 min-[901px]:pt-7">
        <p className={`col-span-full m-0 ${ETICHETTA_ACCESA}`}>I nostri servizi · Il metodo</p>
        <h2 id="pa-mx" className="m-0 font-serif text-[30px] leading-[1.1] font-normal text-ink min-[901px]:text-[38px]">
          Dalla fonte alla decisione.
        </h2>
        <p className="m-0 max-w-[640px] font-sans text-[16px] leading-[1.5] text-sommesso">
          Riconoscere le fonti, governare le informazioni: due manuali aperti per lavorare con l’Osservatorio.
        </p>
      </div>
      <div className="grid grid-cols-1 min-[901px]:grid-cols-2">
        <Colonna
          numero="01"
          etichetta="OSINT"
          titolo="Dalla fonte all’informazione utile."
          disegno={<Imbuto />}
          testo="Un manuale aperto per riconoscere le fonti, distinguere i regimi del messaggio e costruire un prodotto di analisi."
          href="/osint"
          rimando="Leggi il manuale OSINT"
        />
        <Colonna
          numero="02"
          etichetta="Cibernetica aziendale"
          titolo="Il problema ce l’hai già. Il numero arriva dopo."
          disegno={<Anello />}
          testo="Governo dei sistemi, non sicurezza informatica: accorciare la distanza fra il fatto e il numero, e decidere prima."
          href="/cibernetica"
          rimando="Scopri la cibernetica aziendale"
        />
      </div>
    </section>
  );
}

function Colonna({
  numero,
  etichetta,
  titolo,
  disegno,
  testo,
  href,
  rimando,
}: {
  numero: string;
  etichetta: string;
  titolo: string;
  disegno: ReactNode;
  testo: string;
  href: string;
  rimando: string;
}) {
  return (
    <article className="grid grid-cols-1 content-start gap-x-8 gap-y-3 border-rule p-5 not-first:border-t min-[901px]:px-9 min-[901px]:pt-7 min-[901px]:pb-8 min-[901px]:not-first:border-t-0 min-[901px]:not-first:border-l">
      <div className="flex min-w-0 flex-col gap-3">
        <p className={`m-0 flex items-baseline gap-2.5 ${ETICHETTA_ACCESA}`}>
          <span className="font-mono text-segnale">{numero}</span>
          {etichetta}
        </p>
        <h3 className="m-0 font-serif text-[26px] leading-[1.18] font-normal text-ink">{titolo}</h3>
      </div>
      {/* Il disegno fra titolo e testo, fermo a 460 pixel: più largo
          crescerebbe solo in altezza, più stretto le sue scritte (a misura
          del viewBox 300 × 120) scenderebbero sotto i sette pixel. */}
      <div className="w-full max-w-[460px]">{disegno}</div>
      <div className="flex min-w-0 flex-col gap-3">
        <p className="m-0 font-sans text-sommario leading-[1.55] text-sommesso">{testo}</p>
        <Link href={href} className={`mt-1 self-start ${RIMANDO}`}>
          {rimando} <span aria-hidden>↗</span>
        </Link>
      </div>
    </article>
  );
}

const RIQUADRO = "oi-pa-mx my-1.5 block h-auto w-full border border-rule";

/** Lo stile di un quadratino che corre: linea, durata, sfasamento, e la
 *  posa da fermo. */
function corsa(percorso: string, durata: string, ritardo: string, fermo: string): CSSProperties {
  return { offsetPath: `path("${percorso}")`, offsetDistance: fermo, animationDuration: durata, animationDelay: ritardo };
}

/** L'imbuto: le fonti in alto lampeggiano una dopo l'altra, due quadratini
 *  rame scendono fino a «INFORMAZIONE UTILE». */
export function Imbuto() {
  const fonti: [number, number][] = [
    [14, 14], [44, 22], [74, 30], [104, 14], [134, 22], [164, 30], [194, 14], [224, 22], [254, 30],
  ];
  return (
    <svg viewBox="0 0 300 120" aria-hidden className={RIQUADRO}>
      {fonti.map(([x, y], i) => (
        <rect key={x} className="mx-fonte fill-mare" x={x} y={y} width="8" height="8" style={{ animationDelay: `${(i * 0.3).toFixed(1)}s` }} />
      ))}
      <path className="fill-mare stroke-mare" fillOpacity={0.1} strokeOpacity={0.45} d="M8 40 H292 L190 84 H110 Z" />
      <path className="fill-mare" fillOpacity={0.22} d="M110 84 H190 L170 100 H130 Z" />
      <rect className="fill-segnale" x="140" y="104" width="20" height="12" />
      <rect className="mx-corsa fill-segnale" x="-3" y="-3" width="6" height="6" style={corsa("M60 40 L140 84 L150 104", "3.6s", "0s", "40%")} />
      <rect className="mx-corsa fill-segnale" x="-3" y="-3" width="6" height="6" style={corsa("M250 40 L160 84 L150 104", "3.6s", "-1.8s", "75%")} />
      <text x="292" y="56" textAnchor="end" className="mx-lb fill-ink">FONTI</text>
      <text x="168" y="114" className="mx-lb fill-segnale">INFORMAZIONE UTILE</text>
    </svg>
  );
}

/** L'anello: fatto, numero, decisione e la retroazione nel rame, con un
 *  punto rame che gira. */
export function Anello() {
  return (
    <svg viewBox="0 0 300 120" aria-hidden className={RIQUADRO}>
      <circle className="stroke-mare" fill="none" strokeOpacity={0.45} strokeDasharray="3 5" cx="150" cy="60" r="40" />
      <circle className="fill-mare" cx="150" cy="20" r="3.5" />
      <circle className="fill-mare" cx="190" cy="60" r="3.5" />
      <circle className="fill-mare" cx="150" cy="100" r="3.5" />
      <circle className="fill-segnale" cx="110" cy="60" r="5" />
      <circle className="mx-corsa fill-segnale" r="3" style={corsa("M150 20 A40 40 0 1 1 149.9 20", "6s", "0s", "12%")} />
      <text x="150" y="10" textAnchor="middle" className="mx-lb fill-ink">01 FATTO</text>
      <text x="200" y="64" className="mx-lb fill-ink">02 NUMERO</text>
      <text x="150" y="117" textAnchor="middle" className="mx-lb fill-ink">03 DECISIONE</text>
      <text x="100" y="64" textAnchor="end" className="mx-lb fill-segnale">04 RETROAZIONE</text>
    </svg>
  );
}

/** Il posto dello strumento: impresa, persone e processi fanno il sistema;
 *  accanto, il posto tratteggiato, dove il blocco rame «AI» scende ed entra.
 *  Fermo, l'AI resta nel suo posto. */
export function Posto() {
  return (
    <svg viewBox="0 0 300 120" aria-hidden className={RIQUADRO}>
      {[
        [18, "IMPRESA"],
        [50, "PERSONE"],
        [82, "PROCESSI"],
      ].map(([y, t]) => (
        <g key={t}>
          <rect className="fill-surface stroke-mare" strokeOpacity={0.6} x="16" y={y} width="70" height="22" />
          <text x="51" y={(y as number) + 15} textAnchor="middle" className="mx-lb fill-ink">{t}</text>
        </g>
      ))}
      <path className="stroke-mare" fill="none" strokeOpacity={0.5} strokeDasharray="3 3" d="M86 29 H120 V61 M86 61 H120 M86 93 H120 V61" />
      <rect className="fill-surface stroke-segnale" strokeWidth={1.6} x="120" y="44" width="70" height="34" />
      <text x="155" y="65" textAnchor="middle" className="mx-lb fill-ink">SISTEMA</text>
      <path className="stroke-mare" fill="none" strokeOpacity={0.5} strokeDasharray="3 3" d="M190 61 H222" />
      <rect className="stroke-segnale" fill="none" strokeDasharray="4 3" x="222" y="44" width="62" height="34" />
      <g className="mx-ai">
        <rect className="fill-segnale" x="222" y="44" width="62" height="34" />
        <text x="253" y="65" textAnchor="middle" className="fill-surface font-mono" fontSize={12} fontWeight={600}>
          AI
        </text>
      </g>
      <text x="253" y="96" textAnchor="middle" className="mx-lb fill-segnale">PRIMA IL SISTEMA</text>
    </svg>
  );
}
